import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Bot,
  User,
  Loader2,
  Send,
  FileUp,
  CheckCircle2,
  XCircle,
  Sparkles,
  Rocket,
  ArrowRight,
  RotateCcw,
} from 'lucide-react';

/*
 * v1.1.5 — Collaboratore AI Iscrizioni
 * Chat-style wizard che guida l'utente:
 *   1) saluto → richiesta upload Unilav
 *   2) estrazione AI (riusa /ai/bulk-enrollment/extract-unilav-pdf)
 *   3) conferma dati discenti (compatti)
 *   4) scelta piattaforma (chip per ogni piattaforma configurata)
 *   5) suggerimento AI corsi (per ogni discente, basato su mansione)
 *   6) conferma corsi (modifica inline se necessario)
 *   7) launch via /platforms/enroll-from-matrix
 *   8) mostra job_id avviati con link "Job in corso"
 *
 * State machine: phase = 'upload'|'extracting'|'review-data'|'pick-platform'|
 *                'suggest-courses'|'confirm'|'launching'|'done'
 */

const Avatar = ({ kind }) => {
  if (kind === 'bot') {
    return (
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shrink-0 shadow">
        <Bot className="w-5 h-5 text-white" />
      </div>
    );
  }
  return (
    <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
      <User className="w-5 h-5 text-slate-700" />
    </div>
  );
};

const Bubble = ({ kind, children }) => (
  <div className={`flex gap-3 ${kind === 'user' ? 'justify-end' : ''}`} data-testid={`bubble-${kind}`}>
    {kind === 'bot' && <Avatar kind="bot" />}
    <div
      className={`max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
        kind === 'bot'
          ? 'bg-white border border-violet-100 text-slate-800'
          : 'bg-violet-600 text-white'
      }`}
    >
      {children}
    </div>
    {kind === 'user' && <Avatar kind="user" />}
  </div>
);

export default function AgenteCollabIscrizioniPage() {
  const [messages, setMessages] = useState([
    {
      kind: 'bot',
      content:
        "Ciao! Sono il tuo collaboratore AI per le iscrizioni. Trascina qui i PDF Unilav dei tuoi lavoratori (uno o più) e penso a tutto io: estrazione dati, scelta dei corsi obbligatori e iscrizione sulla piattaforma giusta. ✨",
    },
  ]);
  const [phase, setPhase] = useState('upload');
  const [files, setFiles] = useState([]);
  const [discenti, setDiscenti] = useState([]); // [{filename, ok, data, ...}]
  const [platforms, setPlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [coursesPerDiscente, setCoursesPerDiscente] = useState({}); // {idx: [course strings]}
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [jobsResult, setJobsResult] = useState(null);
  const [editingCourseIdx, setEditingCourseIdx] = useState(null);
  const [courseInput, setCourseInput] = useState('');
  const scrollerRef = useRef(null);

  // Autoscroll
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, phase, discenti, coursesPerDiscente]);

  // Health check + load platforms
  const [backendOutdated, setBackendOutdated] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        // Verifica se l'endpoint AI esiste (HEAD/OPTIONS)
        const h = await api.get('/system/routes-status').catch(() => null);
        const loaded = h?.data?.routers?.['routes.ai_enrollment.ai_router'];
        if (!loaded || loaded !== 'loaded') {
          setBackendOutdated(true);
          setMessages((m) => [
            ...m,
            {
              kind: 'bot',
              content:
                "⚠️ ATTENZIONE: il backend del tuo server non è aggiornato — manca il modulo AI estrazione Unilav. Esegui lo script di deploy v1.1.6 prima di caricare i PDF (vedi sotto).",
            },
          ]);
        }
      } catch (e) {
        // ignore — il check è best-effort
      }
      try {
        const r = await api.get('/platforms');
        setPlatforms(r.data || []);
      } catch (e) {
        toast.error('Impossibile caricare le piattaforme configurate');
      }
    })();
  }, []);

  const pushBot = (content) => setMessages((m) => [...m, { kind: 'bot', content }]);
  const pushUser = (content) => setMessages((m) => [...m, { kind: 'user', content }]);

  // ---------- 1) UPLOAD ----------
  const handleFiles = async (incoming) => {
    const fs = Array.from(incoming || []).filter((f) =>
      f.name.toLowerCase().endsWith('.pdf')
    );
    if (fs.length === 0) {
      toast.error('Seleziona almeno un PDF Unilav');
      return;
    }
    if (fs.length > 30) {
      toast.error('Massimo 30 file per volta');
      return;
    }
    setFiles(fs);
    pushUser(`Ho caricato ${fs.length} PDF: ${fs.map((f) => f.name).join(', ')}`);
    setPhase('extracting');
    pushBot(
      `Ricevuti ${fs.length} file. Avvio l'estrazione AI dei dati anagrafici... (può richiedere 30-90 secondi)`
    );
    try {
      const fd = new FormData();
      fs.forEach((f) => fd.append('files', f));
      const r = await api.post('/ai/bulk-enrollment/extract-unilav-pdf', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 240000,
      });
      const oks = (r.data.results || []).filter((x) => x.ok && x.data);
      const fails = (r.data.results || []).filter((x) => !x.ok);
      if (oks.length === 0) {
        pushBot(
          `Mi dispiace, non sono riuscito a estrarre nessun discente. ${
            fails[0]?.error || 'Verifica che i PDF siano Unilav leggibili (non scansioni immagine).'
          }`
        );
        setPhase('upload');
        setFiles([]);
        return;
      }
      setDiscenti(oks);
      pushBot(
        `Fatto! Ho estratto ${oks.length} discent${oks.length === 1 ? 'e' : 'i'}${
          fails.length > 0 ? ` (${fails.length} PDF non leggibili)` : ''
        }. Controlla i dati qui sotto e dimmi se posso procedere.`
      );
      setPhase('review-data');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'errore sconosciuto';
      const isNotFound = String(detail).toLowerCase().includes('not found') || e.response?.status === 404;
      if (isNotFound) {
        setBackendOutdated(true);
        pushBot(
          "🚨 Il backend del tuo server NON è aggiornato (manca l'endpoint AI estrazione Unilav). Vedi il banner giallo in alto: copia il comando, eseguilo via SSH sul tuo VPS, ricarica la pagina e riprova."
        );
      } else {
        pushBot(
          `Ops, qualcosa è andato storto durante l'estrazione: **${detail}**. Riprova tra qualche secondo.`
        );
      }
      setPhase('upload');
      setFiles([]);
    }
  };

  const confirmData = () => {
    pushUser('Sì, i dati sono corretti.');
    if (platforms.length === 0) {
      pushBot(
        'Per procedere serve almeno una piattaforma configurata. Vai su "Agente Iscrizioni" → "+ Aggiungi piattaforma".'
      );
      return;
    }
    pushBot(
      `Su quale piattaforma li devo iscrivere? Hai ${platforms.length} piattaform${platforms.length === 1 ? 'a' : 'e'} configurat${platforms.length === 1 ? 'a' : 'e'}:`
    );
    setPhase('pick-platform');
  };

  const restartUpload = () => {
    pushUser('Voglio caricare PDF diversi');
    setDiscenti([]);
    setFiles([]);
    setCoursesPerDiscente({});
    setJobsResult(null);
    pushBot('Ok, trascina i nuovi PDF Unilav qui sotto.');
    setPhase('upload');
  };

  // ---------- 4) PICK PLATFORM ----------
  const pickPlatform = async (p) => {
    setSelectedPlatform(p);
    pushUser(`Iscriviamoli su ${p.nickname || p.kind}.`);
    pushBot(
      `Perfetto, piattaforma "${p.nickname || p.kind}" selezionata. Ora analizzo la mansione di ciascuno e ti propongo i corsi obbligatori. Un attimo... 🔎`
    );
    setPhase('suggest-courses');
    setLoadingSuggestions(true);
    const result = {};
    for (let i = 0; i < discenti.length; i++) {
      const d = discenti[i].data;
      try {
        const r = await api.post('/ai/iscrizioni-collab/suggest-courses', {
          discente: d,
          available_courses: p.courses || [],
          platform_kind: p.kind,
          max_suggestions: 4,
        });
        result[i] = r.data.suggestions || [];
      } catch (e) {
        result[i] = ['Formazione Generale Lavoratori (4h)'];
      }
    }
    setCoursesPerDiscente(result);
    setLoadingSuggestions(false);
    pushBot(
      'Ecco i corsi che ho proposto per ciascuno. Puoi modificarli (clicca sulla X per togliere, "+ Aggiungi" per aggiungerne altri). Quando sei pronto premi "Iscrivi tutti".'
    );
    setPhase('confirm');
  };

  // ---------- 5) MODIFY COURSES ----------
  const removeCourse = (discenteIdx, courseIdx) => {
    setCoursesPerDiscente((prev) => {
      const list = [...(prev[discenteIdx] || [])];
      list.splice(courseIdx, 1);
      return { ...prev, [discenteIdx]: list };
    });
  };
  const addCourse = (discenteIdx) => {
    const name = courseInput.trim();
    if (!name) return;
    setCoursesPerDiscente((prev) => {
      const list = [...(prev[discenteIdx] || []), name];
      return { ...prev, [discenteIdx]: list };
    });
    setCourseInput('');
    setEditingCourseIdx(null);
  };

  // ---------- 6) LAUNCH ----------
  const launchEnrollment = async () => {
    // Build matrix payload
    const rows = discenti.map((d, idx) => ({
      discente: d.data,
      platform_id: selectedPlatform.id,
      course_names: (coursesPerDiscente[idx] || []).filter((c) => c && c.trim()),
    })).filter((r) => r.course_names.length > 0);

    if (rows.length === 0) {
      toast.error('Assegna almeno un corso ad almeno un discente');
      return;
    }
    pushUser('Vai, iscriviamoli tutti!');
    setLaunching(true);
    setPhase('launching');
    pushBot("Avvio l'agente Playwright in background. Ogni iscrizione diventa un job che puoi monitorare. 🚀");
    try {
      const r = await api.post('/platforms/enroll-from-matrix', {
        rows,
        dry_run: false,
      });
      setJobsResult(r.data);
      pushBot(
        `Fatto! Ho avviato ${r.data.jobs_total} iscrizion${r.data.jobs_total === 1 ? 'e' : 'i'}. Vai su "Agente Iscrizioni" per vedere i log in tempo reale.`
      );
      setPhase('done');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      pushBot(`Errore durante l'avvio: ${detail}`);
      setPhase('confirm');
    } finally {
      setLaunching(false);
    }
  };

  // ---------- RESET ----------
  const reset = () => {
    setMessages([
      {
        kind: 'bot',
        content:
          'Ciao! Sono il tuo collaboratore AI per le iscrizioni. Trascina qui i PDF Unilav dei tuoi lavoratori e penso a tutto io. ✨',
      },
    ]);
    setPhase('upload');
    setFiles([]);
    setDiscenti([]);
    setSelectedPlatform(null);
    setCoursesPerDiscente({});
    setJobsResult(null);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="collab-iscrizioni-page">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Collaboratore AI Iscrizioni</h1>
            <p className="text-sm text-slate-500">Carica gli Unilav, ti guido fino all&apos;iscrizione completata</p>
          </div>
        </div>
        {phase === 'done' && (
          <Button onClick={reset} variant="outline" size="sm" data-testid="restart-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Nuova sessione
          </Button>
        )}
      </div>

      {/* Banner backend outdated */}
      {backendOutdated && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="backend-outdated-banner">
          <div className="flex items-start gap-3">
            <div className="text-amber-600 text-xl">⚠️</div>
            <div className="flex-1">
              <p className="font-semibold text-amber-900 text-sm mb-1">
                Backend non aggiornato — manca il modulo AI estrazione Unilav
              </p>
              <p className="text-xs text-amber-800 mb-2">
                Esegui questo comando in SSH sul tuo VPS per aggiornare a v1.1.6:
              </p>
              <div className="flex items-center gap-2 bg-slate-900 rounded p-2 text-xs font-mono text-emerald-300 overflow-x-auto">
                <span className="select-all">curl -fsSL https://cen-compliance.preview.emergentagent.com/api/download-file/recovery_v1.1.6_safe.sh -o /tmp/r.sh && sudo bash /tmp/r.sh</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-emerald-300 hover:text-emerald-100 h-7 px-2 shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      'curl -fsSL https://cen-compliance.preview.emergentagent.com/api/download-file/recovery_v1.1.6_safe.sh -o /tmp/r.sh && sudo bash /tmp/r.sh'
                    );
                    toast.success('Comando copiato negli appunti');
                  }}
                  data-testid="copy-deploy-cmd"
                >
                  Copia
                </Button>
              </div>
              <p className="text-xs text-amber-700 mt-2">
                Dopo il deploy, ricarica la pagina con <kbd className="bg-white px-1 rounded border">Ctrl+Shift+R</kbd> e riprova.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Chat area */}
      <Card className="border-violet-200 bg-gradient-to-br from-violet-50/30 to-white">
        <CardContent className="p-0">
          <div
            ref={scrollerRef}
            className="max-h-[55vh] min-h-[300px] overflow-y-auto p-5 space-y-4"
            data-testid="chat-scroller"
          >
            {messages.map((m, i) => (
              <Bubble key={i} kind={m.kind}>
                {m.content.split('**').map((seg, idx) =>
                  idx % 2 === 1 ? (
                    <strong key={idx} className="font-semibold">
                      {seg}
                    </strong>
                  ) : (
                    <span key={idx}>{seg}</span>
                  )
                )}
              </Bubble>
            ))}

            {/* Inline UI per fase corrente */}
            {phase === 'upload' && (
              <div className="pl-12">
                <UploadDropzone onFiles={handleFiles} />
              </div>
            )}

            {phase === 'extracting' && (
              <div className="pl-12 flex items-center gap-3 text-violet-600 text-sm" data-testid="extracting-indicator">
                <Loader2 className="w-4 h-4 animate-spin" />
                Estrazione AI in corso...
              </div>
            )}

            {phase === 'review-data' && (
              <div className="pl-12 space-y-3" data-testid="review-data-section">
                {discenti.map((d, i) => (
                  <DiscenteCard key={i} d={d.data} filename={d.filename} />
                ))}
                <div className="flex gap-2 pt-1">
                  <Button onClick={confirmData} className="bg-violet-600 hover:bg-violet-700" data-testid="confirm-data-btn">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Sì, vai avanti
                  </Button>
                  <Button onClick={restartUpload} variant="outline" data-testid="restart-upload-btn">
                    Carica altri PDF
                  </Button>
                </div>
              </div>
            )}

            {phase === 'pick-platform' && (
              <div className="pl-12 flex flex-wrap gap-2" data-testid="platform-picker">
                {platforms.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => pickPlatform(p)}
                    className="px-4 py-2.5 rounded-xl border border-violet-200 bg-white hover:bg-violet-50 hover:border-violet-400 transition flex items-center gap-2 shadow-sm"
                    data-testid={`platform-chip-${p.kind}`}
                  >
                    <span className="font-medium text-slate-800">{p.nickname || p.kind}</span>
                    <Badge variant="outline" className="text-xs">{p.kind}</Badge>
                  </button>
                ))}
              </div>
            )}

            {phase === 'suggest-courses' && loadingSuggestions && (
              <div className="pl-12 flex items-center gap-3 text-violet-600 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Analizzo le mansioni e propongo i corsi...
              </div>
            )}

            {(phase === 'confirm' || phase === 'launching') && (
              <div className="pl-12 space-y-3" data-testid="confirm-section">
                {discenti.map((d, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-200 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <span className="font-semibold text-slate-900">
                          {d.data.cognome} {d.data.nome}
                        </span>
                        <span className="text-slate-500 ml-2 text-xs">{d.data.mansione || '—'}</span>
                      </div>
                      <Sparkles className="w-4 h-4 text-violet-400" />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(coursesPerDiscente[i] || []).map((c, ci) => (
                        <Badge
                          key={ci}
                          className="bg-violet-100 text-violet-800 hover:bg-violet-200 cursor-default pl-2 pr-1 py-1"
                          data-testid={`course-chip-${i}-${ci}`}
                        >
                          {c}
                          <button
                            onClick={() => removeCourse(i, ci)}
                            className="ml-1.5 hover:text-red-600"
                            disabled={launching}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </Badge>
                      ))}
                      {editingCourseIdx === i ? (
                        <div className="flex gap-1 items-center">
                          <input
                            autoFocus
                            value={courseInput}
                            onChange={(e) => setCourseInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addCourse(i)}
                            placeholder="Nome corso..."
                            className="text-xs border border-violet-300 rounded px-2 py-1 outline-none focus:border-violet-500 w-48"
                          />
                          <Button size="sm" variant="ghost" onClick={() => addCourse(i)} className="h-7 px-2">
                            OK
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingCourseIdx(i);
                            setCourseInput('');
                          }}
                          disabled={launching}
                          className="text-xs px-2 py-1 rounded border border-dashed border-violet-300 text-violet-600 hover:bg-violet-50"
                          data-testid={`add-course-btn-${i}`}
                        >
                          + Aggiungi corso
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <Button
                  onClick={launchEnrollment}
                  disabled={launching}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                  data-testid="launch-enrollment-btn"
                >
                  {launching ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Iscrizione in corso...</>
                  ) : (
                    <><Rocket className="w-4 h-4 mr-2" /> Iscrivi tutti</>
                  )}
                </Button>
              </div>
            )}

            {phase === 'done' && jobsResult && (
              <div className="pl-12 space-y-2" data-testid="jobs-result">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-2">
                    <CheckCircle2 className="w-5 h-5" />
                    {jobsResult.jobs_total} iscrizion{jobsResult.jobs_total === 1 ? 'e avviata' : 'i avviate'}
                  </div>
                  <div className="space-y-1 text-xs text-slate-700">
                    {(jobsResult.jobs || []).map((j, i) => (
                      <div key={i} className="font-mono">
                        • {j.discente} → {j.course_name} ({j.platform_nickname})
                      </div>
                    ))}
                  </div>
                  <a
                    href="/studio/piattaforme-formazione"
                    className="inline-flex items-center gap-1 mt-3 text-sm text-violet-700 hover:underline"
                  >
                    Vedi job in corso <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400 mt-3 text-center">
        L&apos;estrazione usa GPT-4o sui PDF Unilav (mai inventa dati, lascia &quot;&quot; se non sicura).
      </p>
    </div>
  );
}

// ---------- Sub-components ----------

function UploadDropzone({ onFiles }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        onFiles(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition ${
        dragging
          ? 'border-violet-500 bg-violet-50'
          : 'border-violet-300 bg-white hover:bg-violet-50/50'
      }`}
      data-testid="collab-drop-zone"
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => onFiles(e.target.files)}
        data-testid="collab-file-input"
      />
      <FileUp className="w-10 h-10 text-violet-400 mx-auto mb-2" />
      <p className="text-sm font-medium text-slate-700">Clicca o trascina i PDF Unilav qui</p>
      <p className="text-xs text-slate-500 mt-1">PDF • max 30 file • estrazione automatica</p>
    </div>
  );
}

function DiscenteCard({ d, filename }) {
  const lines = [
    { k: 'CF', v: d.codice_fiscale },
    { k: 'Mansione', v: d.mansione },
    { k: 'CCNL', v: d.ccnl },
    { k: 'Residenza', v: [d.indirizzo, d.comune_residenza, d.cap].filter(Boolean).join(', ') },
  ].filter((x) => x.v);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="discente-card">
      <div className="flex items-start justify-between mb-1">
        <div className="font-semibold text-slate-900 text-sm">
          {d.cognome} {d.nome}
        </div>
        <div className="text-xs text-slate-400 truncate max-w-[180px]" title={filename}>
          {filename}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-slate-600">
        {lines.map((l, i) => (
          <div key={i}>
            <span className="text-slate-400">{l.k}:</span>{' '}
            <span className="text-slate-700">{l.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
